import { defineStore } from "pinia";
import { progressApi } from "@/services/progressApi";

/**
 * ProgressStore
 * -------------
 * Single source of truth for:
 * - section group progress (accuracy + lastAttempt)
 * - lesson state machine (idle/in_progress/ready/completed)
 * - lesson progress analytics (coverage/confidence/attempts)
 * - continue pointer (resume)
 *
 * Data is persisted to MongoDB Atlas via Lambda API.
 * Uses optimistic updates with background sync for instant UI response.
 *
 * IMPORTANT:
 * - lessonProgress is *analytics* (NOT state), so it is not a duplicate
 */

const BAND_ORDER = ["beginner", "developing", "secure", "confident", "mastery"];

// XP Levels: thresholds for each level
const XP_LEVELS = [0, 100, 250, 500, 1000, 2000, 4000, 8000, 15000, 30000];

// XP rewards configuration
const XP_REWARDS = {
  questionCorrect: 5,        // per correct answer
  lessonComplete: 25,        // finish a lesson
  topicMastery: 15,          // reach mastery on a topic
  revisionComplete: 15,      // complete a revision session
  testComplete: 30,          // complete a test
  diagnosticComplete: 20,    // complete diagnostic
  dailyMissionComplete: 10,  // bonus for all daily tasks
  streakBonus: 5             // per streak day (multiplied)
};

// Debounce timers for cold-path syncs
const debounceTimers = {};

function clamp01(n) {
  return Math.max(0, Math.min(1, n));
}

function bandMin(a, b) {
  return BAND_ORDER.indexOf(a) <= BAND_ORDER.indexOf(b) ? a : b;
}

function nowMs() {
  return Date.now();
}

function isoDate(d = new Date()) {
  return new Date(d).toISOString().slice(0, 10);
}

function getCurrentDay() {
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const today = new Date();
  return days[today.getDay()];
}

function startOfWeekISO(date = new Date()) {
  // Monday-based week start (UK-friendly)
  const d = new Date(date);
  const day = d.getDay(); // 0 Sun..6 Sat
  const diff = (day === 0 ? -6 : 1 - day); // move to Monday
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
}

function getWeekStart(ts) {
  const d = new Date(ts);
  const day = d.getDay(); // 0=Sun
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/**
 * Debounce helper for cold-path syncs
 */
function debounce(key, fn, delay) {
  if (debounceTimers[key]) {
    clearTimeout(debounceTimers[key]);
  }
  debounceTimers[key] = setTimeout(() => {
    delete debounceTimers[key];
    fn();
  }, delay);
}


export const useProgressStore = defineStore("progressStore", {
  state: () => ({
    // continueState[childId] = { lessonId, updatedAt } | null
    continueState: {},
    // [childId]: lessonId
    overrideLesson: {},
    // optional parent preference
    preferences: {},
    dailyStreak: {
      // [childId]: {
      //   count: number,
      //   lastActiveDate: "YYYY-MM-DD"
      // }
    },
    /**
     * progress[childId][sectionGroupId] = {
     *   accuracy: number (0–1),
     *   recentCorrect: boolean,
     *   lastAttempt: timestamp,
     *   attempts?:
     *   difficultyLevel?: "easy" | "medium" | "hard"   // from diagnostic
     * }
     */
    
    progress: {},

    activityLog: {
      // [childId]: [LearningEvent]
      // type LearningEvent = {
      //   id: string
      //   childId: string
      //   type: "lesson" | "revision" | "diagnostic" | "test" | "badge"
      //   title: string
      //   module?: string
      //   lessonId?: string
      //   score?: number
      //   difficulty?: string
      //   timestamp: number
      // }
    },
    revisionHistory: {
      // [childId]: [{ date, score, questionsTotal, questionsCorrect, topics: { [sgId]: { correct, total } } }]
    },
    weeklyTargetsCore: {
      // [childId]: [{
      //   weekStart: number,        // Monday 00:00
      //   plan: {
      //     lessons: number,       // e.g. 5
      //     revisions: number,     // e.g. 5
      //     tests: number          // e.g. 1
      //   },
      //   mode: "curriculum" | "adaptive" | "mixed"}]
    },

    // ─────────────────────────────────────────────────────────────
    // XP System
    // ─────────────────────────────────────────────────────────────
    xp: {
      // [childId]: {
      //   total: number,           // lifetime XP
      //   weeklyXP: number,        // XP earned this week
      //   weekStart: number,       // timestamp of week start
      // }
    },

    // ─────────────────────────────────────────────────────────────
    // Sync metadata
    // ─────────────────────────────────────────────────────────────
    _meta: {
      loaded: {},           // { [childId]: boolean }
      loading: {},          // { [childId]: boolean } - prevents duplicate loads
      progressVersion: {},  // { [childId]: number }
      stateVersion: {},     // { [childId]: number }
      pendingWrites: [],    // Failed writes to retry
      syncing: false
    },

    // ─────────────────────────────────────────────────────────────
    // Pending progress updates (batched until lesson finishes)
    // { [childId]: { [sectionGroupId]: { correct: boolean, count: number } } }
    // ─────────────────────────────────────────────────────────────
    _pendingProgressUpdates: {}
  }),

  getters: {
    getChildProgress: (state) => (childId) => state.progress?.[childId] || {},

    getContinueLesson: (state) => (childId) => state.continueState?.[childId] || null,

    getOverrideLesson: (state) => (childId) => {
      return state.overrideLesson[childId] || null;
    },

    hasCompletedLessonToday: (state) => (childId, dayKey) => {
      // dayKey is 'YYYY-MM-DD'
      const log = state.activityLog?.[childId] || [];
      return log.some(e => {
        if (e.type !== 'lesson') return false;
        const d = new Date(e.timestamp);
        const ymd = d.toISOString().slice(0, 10);
        return ymd === dayKey;
      });
    },

    hasCompletedRevisionToday: (state) => (childId, dayKey) => {
      const log = state.activityLog?.[childId] || [];
      return log.some(e => {
        if (e.type !== 'revision') return false;
        const d = new Date(e.timestamp);
        const ymd = d.toISOString().slice(0, 10);
        return ymd === dayKey;
      });
    },

    hasCompletedTestToday: (state) => (childId, dayKey) => {
      const log = state.activityLog?.[childId] || [];
      return log.some(e => {
        if (e.type !== 'test') return false;
        const d = new Date(e.timestamp);
        const ymd = d.toISOString().slice(0, 10);
        return ymd === dayKey;
      });
    },

    getWeeklyTargetsCore(childId) {
      return this.weeklyTargetsCore[childId] || null;
    },

    /**
     * Section-group band derived from accuracy, with optional diagnostic prior.
     * NOTE: this reads progress[childId][sectionGroupId].accuracy, which should *not*
     * jump to 1 after one correct answer.
     */
    getSectionGroupBand: (state) => (childId, sectionGroupId) => {
      const p = state.progress?.[childId]?.[sectionGroupId];
      if (!p) return "beginner";

      const prior = (p.difficultyLevel ?? "").toString().trim().toLowerCase(); // optional "easy|medium|hard"
      const acc = typeof p.accuracy === "number" ? clamp01(p.accuracy) : 0.35;

      let band =
        acc < 0.40 ? "beginner" :
        acc < 0.65 ? "developing" :
        acc < 0.75 ? "secure" :
        acc < 0.86 ? "confident" :
        "mastery";

      // Guard: don't jump to mastery from very few attempts
      const attempts = typeof p.attempts === "number" ? p.attempts : 0;
      if (attempts > 0 && attempts < 3) band = bandMin(band, "confident");

      // Avoid over-claiming early if diagnostic indicated low starting level
      if (prior === "easy") band = bandMin(band, "secure");
      if (prior === "medium") band = bandMin(band, "confident");
      // prior "hard" allows full range

      return band;
    },

    // NEW weekly getters

    getActivityLog: (state) => (childId) => {
      return state.activityLog?.[childId] || [];
    },

    getRevisionHistory: (state) => (childId) => {
      return state.revisionHistory?.[childId] || [];
    },

    getRevisionQuestionCount: (state) => (childId) => {
      return state.preferences?.[childId]?.revisionQuestionCount || 10;
    },

    // Meta getters
    isLoaded: (state) => (childId) => !!state._meta.loaded[childId],
    isLoading: (state) => (childId) => !!state._meta.loading[childId],

    // XP getter with computed level and progress
    getXP: (state) => (childId) => {
      const xpData = state.xp?.[childId] || { total: 0, weeklyXP: 0, weekStart: 0 };
      // Find current level (first threshold that total XP is less than)
      let level = XP_LEVELS.findIndex(threshold => xpData.total < threshold);
      if (level === -1) level = XP_LEVELS.length; // max level
      if (level === 0) level = 1; // minimum level 1

      const currentThreshold = XP_LEVELS[level - 1] || 0;
      const nextThreshold = XP_LEVELS[level] || XP_LEVELS[XP_LEVELS.length - 1];
      const xpInLevel = xpData.total - currentThreshold;
      const xpNeeded = nextThreshold - currentThreshold;

      return {
        total: xpData.total,
        weeklyXP: xpData.weeklyXP,
        level,
        currentThreshold,
        nextThreshold,
        progressToNext: xpNeeded > 0 ? xpInLevel / xpNeeded : 1
      };
    },
  },

  actions: {
    ensureChild(childId) {
      if (!childId) return;

      if (!this.progress[childId]) this.progress[childId] = {};
      if (!this.preferences[childId]) this.preferences[childId] = {};
      if (!this.continueState[childId]) this.continueState[childId] = null;
      if (!this.overrideLesson[childId]) this.overrideLesson[childId] = null;
      if (!this.dailyStreak[childId]) this.dailyStreak[childId] = { count: 0, lastActiveDate: null };
      if (!this.weeklyTargetsCore) this.weeklyTargetsCore = {};
      if (!this.weeklyTargetsCore[childId]) this.weeklyTargetsCore[childId] = null;
      if (!this.activityLog[childId]) this.activityLog[childId] = [];
      if (!this.revisionHistory[childId]) this.revisionHistory[childId] = [];
      if (!this.xp) this.xp = {};
      if (!this.xp[childId]) this.xp[childId] = { total: 0, weeklyXP: 0, weekStart: getWeekStart(Date.now()) };
    },

    /**
     * Seed section-group priors from a completed diagnostic.
     * - Never reduces a topic that already has a secureFloor or higher accuracy.
     * - Marks the child as loaded so loadProgress() doesn't overwrite the seeds.
     * - Schedules a patch so seeds are persisted to the API immediately.
     *
     * @param {string} childId
     * @param {Object} results  — diagnosticReport.results: { [sgId]: { difficultyLevel, attempts } }
     */
    seedFromDiagnostic(childId, results) {
      if (!childId || !results) return;

      this.ensureChild(childId);

      const SEED_ACCURACY = { easy: 0.40, medium: 0.65, hard: 0.78 };

      for (const [sgId, r] of Object.entries(results)) {
        // Use _seedAccuracy override if present (e.g. untested topics from early stopping)
        const seedAccuracy = r._seedAccuracy ?? SEED_ACCURACY[r.difficultyLevel] ?? 0.4;
        const existing = this.progress[childId][sgId];

        // Never reduce a topic that already has a secure floor or higher accuracy
        if (existing?.secureFloor && seedAccuracy < existing.accuracy) {
          existing.lastAttempt = Date.now();
          continue;
        }
        if (existing && typeof existing.accuracy === 'number' && seedAccuracy < existing.accuracy) {
          existing.lastAttempt = Date.now();
          continue;
        }

        this.progress[childId][sgId] = {
          ...(existing || {}),
          accuracy: seedAccuracy,
          difficultyLevel: r.difficultyLevel,
          attempts: (existing?.attempts || 0) + (r.attempts || 1),
          lastAttempt: Date.now(),
        };
      }

      // Mark as loaded so loadProgress() won't overwrite with empty API data
      this._meta.loaded[childId] = true;

      // Persist seeds directly to the progress document
      progressApi.seedProgress(childId, this.progress[childId])
        .catch(err => console.warn('Failed to persist diagnostic seeds:', err));
    },

    // ─────────────────────────────────────────────────────────────
    // API Integration: Load Progress
    // ─────────────────────────────────────────────────────────────

    /**
     * Load all progress data from API for a child.
     * Fetches all 3 documents in parallel (progress, activity, state).
     * @param {string} childId
     */
    async loadProgress(childId) {
      if (!childId) return;

      // Skip if already loaded or currently loading
      if (this._meta.loaded[childId] || this._meta.loading[childId]) {
        return;
      }

      this._meta.loading[childId] = true;

      try {
        const data = await progressApi.loadAll(childId);

        // Hydrate state from 3 documents
        // Apply secure floor: server doesn't enforce the floor, so clamp here
        const progress = data.progress || {};
        for (const [, p] of Object.entries(progress)) {
          if (p.secureFloor && typeof p.accuracy === 'number' && p.accuracy < p.secureFloor) {
            p.accuracy = p.secureFloor;
          }
        }
        this.progress[childId] = progress;
        this.activityLog[childId] = data.activityLog || [];
        this.continueState[childId] = data.continueState || null;
        this.overrideLesson[childId] = data.overrideLesson || null;
        this.preferences[childId] = data.preferences || {};
        this.dailyStreak[childId] = data.dailyStreak || { count: 0, lastActiveDate: null };
        this.weeklyTargetsCore[childId] = data.weeklyTargetsCore || [];
        this.revisionHistory[childId] = data.revisionHistory || [];
        if (data.xp) this.xp[childId] = data.xp;

        // Store versions for optimistic locking
        this._meta.progressVersion[childId] = data.progressVersion || 1;
        this._meta.stateVersion[childId] = data.stateVersion || 1;
        this._meta.loaded[childId] = true;

      } catch (error) {
        console.warn('Failed to load progress from API, using empty state:', error);
        this.ensureChild(childId);
      } finally {
        this._meta.loading[childId] = false;
      }
    },

    // ─────────────────────────────────────────────────────────────
    // Activity Log
    // ─────────────────────────────────────────────────────────────

    addActivity(childId, event) {
      if (!this.activityLog[childId]) {
        this.activityLog[childId] = [];
      }

      const activityEntry = {
        ...event,
        id: crypto.randomUUID(),
        timestamp: Date.now()
      };

      // 1. Optimistic local update (instant UI)
      this.activityLog[childId].unshift(activityEntry);

      // Keep last N events
      this.activityLog[childId] = this.activityLog[childId].slice(0, 50);

      // 2. Background sync to API
      progressApi.addActivity(childId, activityEntry)
        .catch(err => {
          console.warn('Failed to sync activity, queuing for retry:', err);
          this._queuePendingWrite({ type: 'activity', childId, event: activityEntry });
        });
    },

    addRevisionHistory(childId, summary) {
      if (!this.revisionHistory[childId]) {
        this.revisionHistory[childId] = [];
      }
      this.revisionHistory[childId].unshift(summary);
      // Keep last 20
      this.revisionHistory[childId] = this.revisionHistory[childId].slice(0, 20);

      this._schedulePatch(childId, { revisionHistory: this.revisionHistory[childId] }, 1000);
    },

    // TODO: FOR test/mock exam to add
    // progressStore.addActivity(childId, {
    //   type: "test",
    //   title: "Mock Exam",
    //   score: percentScore
    // });


/* -----------------------------
       WEEKLY TARGETS (NEW)
    ------------------------------ */
    ensureWeekly(childId, { targets, mode } = {}) {
      this.ensureChild(childId);

      const wkStart = getWeekStart(new Date());
      if (!this.weeklyTargetsCore[childId] || !Array.isArray(this.weeklyTargetsCore[childId])) {
        this.weeklyTargetsCore[childId] = [];
      }
      const weekArr = this.weeklyTargetsCore[childId];
      // Find if weekStart already exists
      let weekObj = weekArr.find(w => w.weekStart === wkStart);
      if (weekObj) {
        // allow updating targets/mode if provided
        if (targets) weekObj.plan = { ...weekObj.plan, ...targets };
        if (mode) weekObj.mode = mode;
      } else {
        // Insert new default plan for this week
        const newPlan = this.defaultWeekyPlan(wkStart, { mode });
        weekArr.push(newPlan);
        // Debounced sync to API
        this._schedulePatch(childId, { weeklyTargetsCore: this.weeklyTargetsCore[childId] }, 1000);
      }


      
    },

    defaultWeekyPlan(wkStart, {mode} = {}) {
      return {
        weekStart: wkStart,
        plan: {
          lessons: 5,
          revisions: 5,
          tests: 1,
          diagnostics: 0
        },
        mode: mode || "mixed",
      };
    },

    setOverrideLesson(childId, lessonId) {
      this.overrideLesson[childId] = lessonId;

      // Debounced sync to API
      this._schedulePatch(childId, { overrideLesson: lessonId }, 500);
    },

    clearOverrideLesson(childId) {
      delete this.overrideLesson[childId];

      // Debounced sync to API
      this._schedulePatch(childId, { overrideLesson: null }, 500);
    },

    setPreference(childId, patch) {
      this.ensureChild(childId);
      this.preferences[childId] = { ...(this.preferences[childId] || {}), ...(patch || {}) };

      // Debounced sync to API
      this._schedulePatch(childId, { preferences: this.preferences[childId] }, 1000);
    },

    awardBadge(childId, badgeTitle) {
      this.addActivity(childId, {
        type: "badge",
        title: badgeTitle
      });
    },

    /**
     * Award XP to a student.
     * Tracks total and weekly XP, resets weekly on new week.
     * @param {string} childId - Student ID
     * @param {number} amount - XP amount to award
     * @param {string} reason - Reason for XP (for logging/debugging)
     * @returns {{ amount: number, total: number, levelUp: boolean }}
     */
    awardXP(childId, amount, reason) {
      this.ensureChild(childId);

      const currentWeekStart = getWeekStart(Date.now());

      // Reset weekly XP if new week
      if (this.xp[childId].weekStart !== currentWeekStart) {
        this.xp[childId].weeklyXP = 0;
        this.xp[childId].weekStart = currentWeekStart;
      }

      // Calculate old level for level-up detection
      const oldLevel = this.getXP(childId).level;

      // Award XP
      this.xp[childId].total += amount;
      this.xp[childId].weeklyXP += amount;

      // Calculate new level
      const newLevel = this.getXP(childId).level;
      const levelUp = newLevel > oldLevel;

      // Debounced sync to API
      this._schedulePatch(childId, { xp: this.xp[childId] }, 1000);

      return { amount, total: this.xp[childId].total, levelUp };
    },

    awardLessonBadge(childId, lessonId, lessonTitle, band) {
      this.ensureChild(childId);

      const BAND_FOR_BADGE = {
        beginner: "first_steps",
        developing: "growing_learner",
        secure: "solid_skills",
        confident: "confident_thinker",
        mastery: "mastery_achieved"
      };
      const BADGE_TO_BAND = {
        first_steps: 'beginner',
        growing_learner: 'developing',
        solid_skills: 'secure',
        confident_thinker: 'confident',
        mastery_achieved: 'mastery'
      };

      const badge = BAND_FOR_BADGE[band];
      if (!badge) return;

      // Derive fromBand from the most recent badge entry for this lesson
      const prevEntry = (this.activityLog[childId] || [])
        .find(e => e.type === 'badge' && e.lessonId === lessonId);
      const fromBand = prevEntry?.toBand
        || (prevEntry?.badge ? BADGE_TO_BAND[prevEntry.badge] : null)
        || 'beginner';

      this.addActivity(childId, {
        type: "badge",
        title: `Lesson ${band === 'mastery' ? 'Mastered' : 'Completed'}: ${lessonTitle}`,
        lessonId: lessonId,
        badge: badge,
        fromBand,
        toBand: band
      });
    },

    // -----------------------------
    // Continue pointer
    // -----------------------------
    setContinueLesson(childId, lessonId) {
      this.ensureChild(childId);
      if (!lessonId) return;

      // Note: we intentionally do NOT block on isLessonCompleted (badge) here,
      // because badges can be awarded at session-end even when not all topics are
      // at secure level. The call site (useContinueLearningEngine) decides whether
      // a lesson is truly done using isLessonReady (all topics at secure band).

      // Optimistic update
      this.continueState[childId] = { lessonId, updatedAt: nowMs() };

      // Debounced sync to API
      this._schedulePatch(childId, { continueState: this.continueState[childId] }, 500);
    },

    clearContinueLesson(childId) {
      this.ensureChild(childId);
      this.continueState[childId] = null;

      // Debounced sync to API
      this._schedulePatch(childId, { continueState: null }, 500);
    },

    isLessonCompleted(childId, lessonId) {
      const log = this.activityLog[childId] || [];
      return log.some(a => a.type === "badge" && a.lessonId === lessonId);
    },

    /**
     * Update section-group progress using a stable moving average.
     * IMPORTANT: do NOT set accuracy to 1 for a single correct response.
     *
     * Progress updates are batched in memory until flushLessonProgress() is called.
     * This reduces API calls during a lesson.
     */
    updateProgress(childId, sectionGroupId, correct, { sectionTitle } = {}) {
      const now = nowMs();
      this.ensureChild(childId);
      if (!sectionGroupId) return;

      const p = this.progress[childId][sectionGroupId] || {
        accuracy: 0.45,         // neutral prior
        recentCorrect: null,
        lastAttempt: 0,
        attempts: 0
      };

      p.attempts = (p.attempts || 0) + 1;

      const stepUp = 0.08;
      const stepDown = 0.10;

      // Once a topic reaches "secure" (0.65), lock that as the floor permanently.
      if (p.accuracy >= 0.65 && !p.secureFloor) {
        p.secureFloor = 0.65;
      }
      const floor = p.secureFloor || 0;

      p.accuracy = correct
        ? clamp01(p.accuracy + stepUp)
        : Math.max(floor, clamp01(p.accuracy - stepDown));

      p.recentCorrect = !!correct;
      p.lastAttempt = now;

      // 1. Optimistic local update (instant UI)
      this.progress[childId][sectionGroupId] = p;

      // 2. Queue for batch sync (no immediate API call)
      if (!this._pendingProgressUpdates[childId]) {
        this._pendingProgressUpdates[childId] = {};
      }
      if (!this._pendingProgressUpdates[childId][sectionGroupId]) {
        this._pendingProgressUpdates[childId][sectionGroupId] = [];
      }
      this._pendingProgressUpdates[childId][sectionGroupId].push(correct);
    },

    /**
     * Flush all pending progress updates to the API.
     * Call this when a lesson is completed.
     * Uses batch API to send all updates in a single request.
     * @param {string} childId
     */
    async flushLessonProgress(childId) {
      if (!childId) return;

      const pending = this._pendingProgressUpdates[childId];
      if (!pending || Object.keys(pending).length === 0) return;

      // Clear pending updates for this child
      this._pendingProgressUpdates[childId] = {};

      // Flatten all updates into a single array for batch API
      const updates = [];
      for (const [sectionGroupId, correctAnswers] of Object.entries(pending)) {
        for (const correct of correctAnswers) {
          updates.push({ sectionGroupId, correct });
        }
      }

      if (updates.length === 0) return;

      // Single batch API call (instead of N individual calls)
      try {
        await progressApi.updateProgressBatch(childId, updates);
      } catch (err) {
        console.warn('Failed to sync batch progress, queuing for retry:', err);
        // Queue individual updates for retry
        for (const update of updates) {
          this._queuePendingWrite({
            type: 'progress',
            childId,
            sectionGroupId: update.sectionGroupId,
            correct: update.correct
          });
        }
      }
    },

    // ─────────────────────────────────────────────────────────────
    // Internal: Sync helpers
    // ─────────────────────────────────────────────────────────────

    /**
     * Schedule a debounced PATCH to /state/{childId}.
     * Multiple calls before the debounce fires are merged into one payload.
     */
    _schedulePatch(childId, updates, delay = 500) {
      const key = `patch_${childId}`;
      // Merge into pending payload so rapid successive calls don't overwrite each other
      if (!this._meta.pendingPatches) this._meta.pendingPatches = {};
      this._meta.pendingPatches[key] = { ...(this._meta.pendingPatches[key] || {}), ...updates };
      debounce(key, () => {
        const merged = this._meta.pendingPatches[key];
        delete this._meta.pendingPatches[key];
        progressApi.patchState(childId, merged)
          .catch(err => {
            console.warn('Failed to sync state patch, queuing for retry:', err);
            this._queuePendingWrite({ type: 'state', childId, updates: merged });
          });
      }, delay);
    },

    /**
     * Queue a failed write for later retry
     */
    _queuePendingWrite(write) {
      this._meta.pendingWrites.push({
        ...write,
        queuedAt: Date.now()
      });

      // Persist to localStorage for offline recovery
      this._persistPendingWrites();
    },

    /**
     * Persist pending writes to localStorage
     */
    _persistPendingWrites() {
      try {
        localStorage.setItem('progress_pending_writes', JSON.stringify(this._meta.pendingWrites));
      } catch (e) {
        console.warn('Failed to persist pending writes:', e);
      }
    },

    /**
     * Load pending writes from localStorage on startup
     */
    _loadPendingWrites() {
      try {
        const stored = localStorage.getItem('progress_pending_writes');
        if (stored) {
          this._meta.pendingWrites = JSON.parse(stored);
        }
      } catch (e) {
        console.warn('Failed to load pending writes:', e);
      }
    },

    /**
     * Flush all pending writes (called on reconnect)
     */
    async flushPendingWrites() {
      if (this._meta.syncing || this._meta.pendingWrites.length === 0) return;

      this._meta.syncing = true;
      const writes = [...this._meta.pendingWrites];
      this._meta.pendingWrites = [];

      for (const write of writes) {
        try {
          if (write.type === 'progress') {
            await progressApi.updateProgress(write.childId, write.sectionGroupId, write.correct);
          } else if (write.type === 'activity') {
            await progressApi.addActivity(write.childId, write.event);
          } else if (write.type === 'state') {
            await progressApi.patchState(write.childId, write.updates);
          }
        } catch (err) {
          console.warn('Failed to flush pending write, re-queuing:', err);
          this._meta.pendingWrites.push(write);
        }
      }

      this._persistPendingWrites();
      this._meta.syncing = false;
    },

    /**
     * Initialize store: load pending writes from localStorage
     */
    init() {
      this._loadPendingWrites();
    }
  }
});
